import type { Content } from '../data/schema';
import type { Meta } from '../net/progress';
import type { MetaBonuses } from '../core/meta/bonuses';
import { bonusesFrom } from '../core/meta/talents';

/** Nombre maximal de modificateurs de salle actifs (aligné sur le cœur). */
const MAX_ROOM_MODIFIERS = 2;

/** Le talent `id` s'applique-t-il à la classe jouée ? Tronc commun pour toutes, branche de classe pour la sienne seulement. */
function appliesTo(content: Content, id: string, classId: string): boolean {
  const def = Object.hasOwn(content.talents, id) ? content.talents[id] : undefined;
  return !!def && (def.branch === 'commun' || def.branch === classId);
}

/** Rangs des talents qui s'appliquent à `classId` (les autres branches sont ignorées). */
function ranksFor(content: Content, meta: Meta, classId: string): Record<string, number> {
  return Object.fromEntries(Object.entries(meta.talents).filter(([id]) => appliesTo(content, id, classId)));
}

/** Somme des rangs de talents qui s'appliquent à la classe jouée (reprise dans le résumé de run). */
export function talentsOwned(meta: Meta, content: Content, classId: string): number {
  return Object.values(ranksFor(content, meta, classId)).reduce((a, r) => a + (Number.isFinite(r) && r > 0 ? Math.floor(r) : 0), 0);
}

/**
 * Bonus de méta d'une run de `classId` : talents du tronc commun et de la branche de cette classe, sorts et panoplies débloqués,
 * premier familier possédé, modificateurs de salle actifs ET débloqués (deux au plus, dans l'ordre choisi par le joueur).
 */
export function metaBonusesOf(content: Content, meta: Meta, classId: string): MetaBonuses {
  const owned = new Set(meta.unlocks);
  const mine = Object.values(content.unlockables).filter((u) => owned.has(u.id));
  const targets = (kind: 'spellPool' | 'set'): string[] => [...new Set(mine.filter((u) => u.kind === kind).flatMap((u) => u.target))];
  const pet = mine.find((u) => u.kind === 'pet')?.target[0] ?? null;
  const unlockedMods = new Set(mine.filter((u) => u.kind === 'modifier').flatMap((u) => u.target));
  const roomModifiers = [...new Set(meta.activeModifiers)].filter((id) => unlockedMods.has(id)).slice(0, MAX_ROOM_MODIFIERS);
  return { ...bonusesFrom(content.talents, ranksFor(content, meta, classId)), unlockedSpells: targets('spellPool'), unlockedSets: targets('set'), pet, roomModifiers };
}

/** Noms affichables des modificateurs de salle d'une run (ids inconnus ignorés). */
export function modifierNames(content: Content, bonuses: Pick<MetaBonuses, 'roomModifiers'> | undefined): string[] {
  return (bonuses?.roomModifiers ?? []).flatMap((id) => { const m = content.modifiers[id]; return m ? [m.name] : []; });
}

/** Identifiant client d'une run (dédoublonnage à l'enregistrement) : UUID v4. */
export function newClientId(): string {
  const native = globalThis.crypto?.randomUUID?.();
  if (native) return native;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 3) | 8).toString(16);
  });
}
